import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { SETTINGS_TABS, findActiveSection, activeSettingsTab, visibleSections } from './nav'
import { useOrg } from '../org'
import { Icon } from '../brand/Icon'
import { ContextBlock } from './ContextBlock'
import { useT } from '../i18n'
import { OverlaysBlock } from './OverlaysBlock'

interface RailAccordionProps {
  collapsed: boolean
  onNavigate?: () => void
  /** callback-ref DOM-слота инструментов активного раздела (портал страницы). */
  toolsRef?: (el: HTMLDivElement | null) => void
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden
      className={[
        'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
        open ? 'rotate-90 text-sonar' : 'text-faint',
      ].join(' ')}
    >
      <path
        d="M6 4l4 4-4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Рельс-гармошка: три режима-раздела (Карты/Модели/Настройки) как складные секции.
 * Активный режим «зажжён» (курс-синь + инсет-рамка) и раскрыт в колонку инструментов
 * на «лотовой линии» (вертикальный борд слева). Клик по чужому режиму — переход;
 * по активному — свернуть/развернуть его тело.
 */
export function RailAccordion({ collapsed, onNavigate, toolsRef }: RailAccordionProps) {
  const t = useT()
  const { pathname } = useLocation()
  // Разделы, проданные активному кабинету: «Рынок» — только по признаку.
  const sections = visibleSections(useOrg().activeOrg?.services)
  const navigate = useNavigate()
  const active = findActiveSection(pathname)
  const [openId, setOpenId] = useState<string | null>(active?.id ?? null)
  // Смена маршрута → раскрываем новый активный режим.
  useEffect(() => {
    if (active) setOpenId(active.id)
  }, [active?.id])

  // Свёрнутый рельс — только иконки; тела не раскрываются (страница показывает
  // инструменты плавающей картой поверх карты, как и раньше).
  if (collapsed) {
    return (
      <nav className="space-y-1 px-2 py-3">
        {sections.map(s => (
          <NavLink
            key={s.id}
            to={s.path}
            onClick={onNavigate}
            title={s.experimental ? `${t(s.labelKey)} * ${t('nav.experimental')}` : t(s.labelKey)}
            className={({ isActive }) =>
              [
                'flex items-center justify-center rounded-xl px-2.5 py-2.5 transition-colors',
                isActive
                  ? 'bg-sonar/12 text-foam shadow-[inset_0_0_0_1px_rgb(var(--accent))]'
                  : 'text-mist hover:bg-haze hover:text-foam',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <Icon name={s.icon} className={['h-5 w-5', isActive ? 'text-sonar' : ''].join(' ')} />
            )}
          </NavLink>
        ))}
      </nav>
    )
  }

  // Блок «Контекст» (дата·глубина·вид) — общий над разделами карт. В «Настройках»
  // он не нужен (там нет карты).
  const showContext = active?.id === 'maps' || active?.id === 'models'

  return (
    <>
    {showContext && <ContextBlock />}
    {showContext && <OverlaysBlock />}
    <nav className="space-y-1.5 px-2 py-3">
      {sections.map(section => {
        const isActive = section.id === active?.id
        const open = isActive && openId === section.id
        return (
          <div key={section.id}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => {
                if (isActive) setOpenId(o => (o === section.id ? null : section.id))
                else {
                  navigate(section.path)
                  onNavigate?.()
                }
              }}
              className={[
                'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left transition-colors',
                isActive
                  ? 'bg-sonar/12 text-foam shadow-[inset_0_0_0_1px_rgb(var(--accent))]'
                  : 'text-mist hover:bg-haze hover:text-foam',
              ].join(' ')}
            >
              <Icon
                name={section.icon}
                className={['h-5 w-5 shrink-0', isActive ? 'text-sonar' : ''].join(' ')}
              />
              <span className={['flex-1 text-sm', isActive ? 'font-medium' : ''].join(' ')}>
                {t(section.labelKey)}
                {section.experimental && <sup className="ml-0.5 text-faint">*</sup>}
              </span>
              <Chevron open={open} />
            </button>

            {/* Тело активного режима держим смонтированным (слот инструментов не должен
                терять портал при сворачивании) — прячем через hidden. */}
            {isActive && (
              <div className={open ? 'animate-[accordion-in_0.2s_ease] pl-2 pt-1' : 'hidden'}>
                <div className="border-l border-hull pl-2.5">
                  {section.id === 'settings' ? (
                    <SettingsRailNav onNavigate={onNavigate} />
                  ) : (
                    <div ref={toolsRef} />
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
      {sections.some(s => s.experimental) && (
        <p className="px-2.5 pt-2 text-xs text-faint">* {t('nav.experimental')}</p>
      )}
    </nav>
    </>
  )
}

function SettingsRailNav({ onNavigate }: { onNavigate?: () => void }) {
  // Имя `tr`, а не `t`: ниже `tab` — элемент SETTINGS_TABS, и путать их нельзя.
  const tr = useT()
  const { pathname } = useLocation()
  const current = activeSettingsTab(pathname)
  return (
    <div className="space-y-0.5 py-1">
      {SETTINGS_TABS.map(tab => (
        <NavLink
          key={tab.id}
          to={tab.path}
          replace
          onClick={onNavigate}
          className={[
            'block rounded-lg px-2.5 py-1.5 text-sm transition-colors',
            current === tab.id ? 'bg-sonar/10 font-medium text-foam' : 'text-mist hover:text-foam',
          ].join(' ')}
        >
          {tr(tab.labelKey)}
        </NavLink>
      ))}
    </div>
  )
}
